import { createFileRoute } from '@tanstack/react-router'

import {
  Layout,
  Text,
  Button,
  ButtonGroup,
  BlockStack,
  Card,
  MediaCard,
  List,
  Box,
  Image,
} from '@shopify/polaris'

import { PageFrame } from '@/components/shared/PageFrame'
import SetupBanner from '@/components/shared/SetupBanner'

// Utils
import { useSuspenseQuery } from '@tanstack/react-query'
import { settingsQueryOptions } from '@/common/queries'
import useMutateSettings from '@/hooks/useMutateSettings'
import { openBeacon } from '@/common/functions'
import { Onboarding } from '@/components/shared/Onboarding'
import DashAnalytics from '@/components/shared/DashAnalytics'

export const Route = createFileRoute('/')({
  loader: async ({ context: { queryClient }, route: { path } }) => {
    try {
      // Pre-fetch data
      Promise.all([queryClient.ensureQueryData(settingsQueryOptions())])
    } catch (error) {
      console.error('Error in page loader.', path, error)
      throw error
    }
  },
  pendingComponent: Pending,
  component: Page,
})

function Page() {
  const { data: settings } = useSuspenseQuery(settingsQueryOptions())

  const settingsMutation = useMutateSettings()

  // TODO: Don't love this
  const { enabled, themeVersion, methods, onboarded } = settings

  return (
    <PageFrame title={`Bloom: Delivery Date & Pickups`}>
      {!onboarded && <Onboarding />}

      <SetupBanner
        enabled={enabled}
        toggleEnabled={() => {
          settingsMutation.mutate({ enabled: !enabled })
        }}
        isLoading={settingsMutation.isPending}
        themeVersion={themeVersion}
        methods={methods}
        onboarded={onboarded}
      />

      <DashAnalytics />

      <Layout.Section variant="oneHalf">
        <BlockStack gap="400">
          <MediaCard
            title="Getting started with Bloom"
            primaryAction={{
              content: 'Learn more',
              url: 'https://www.shopside.com.au/bloom-article/getting-started-with-bloom',
              target: '_blank',
            }}
            description={`Learn how to configure and activate Bloom on your store.`}
          >
            <Image
              alt="A Bloom branded cover image, showing a computer at a desk."
              width="100%"
              height="100%"
              style={{
                objectFit: 'cover',
                objectPosition: 'center',
              }}
              source="/bloom-hero.webp"
            />
          </MediaCard>
          <Card>
            <BlockStack gap="400">
              <BlockStack gap="200">
                <Text variant="headingSm" fontWeight="bold" as="h2">
                  Get help
                </Text>
                <Text variant="bodyMd" as="p">
                  Whether you need help getting setup with Bloom, or you have a
                  request for a new feature, we&apos;re here to help.
                </Text>
                <ButtonGroup fullWidth>
                  <Button
                    url="https://www.shopside.com.au/docs/bloom"
                    target="_blank"
                  >
                    Visit help center
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => {
                      openBeacon('ASK')
                    }}
                  >
                    Contact support
                  </Button>
                </ButtonGroup>
              </BlockStack>
            </BlockStack>
          </Card>
        </BlockStack>
      </Layout.Section>

      <Layout.Section variant="oneHalf">
        <BlockStack gap="400">
          <Card>
            <BlockStack gap="400">
              <BlockStack gap="200">
                <Text variant="headingSm" fontWeight="bold" as="h2">
                  Frequently asked questions
                </Text>
              </BlockStack>
              <BlockStack gap="200">
                <Text variant="headingSm" fontWeight="medium" as="h3">
                  Why isn&apos;t Bloom showing on my store?
                </Text>
                <Text variant="bodyMd" as="p">
                  Check that Bloom is activated and you have activated at least
                  one delivery method. Ensure you have set up your active days
                  for that method.
                </Text>
                <Text variant="bodyMd" as="p">
                  For OS2.0 themes, ensure you have added the app block to your
                  theme. For vintage themes, check your placements settings.
                </Text>
                <ButtonGroup>
                  <Button
                    variant="plain"
                    target="_blank"
                    url="https://www.shopside.com.au/bloom-article/why-isnt-bloom-showing-on-my-store"
                  >
                    Learn more
                  </Button>
                </ButtonGroup>
              </BlockStack>
              <BlockStack gap="200">
                <Text variant="headingSm" fontWeight="medium" as="h3">
                  Why did I receive an order with no date?
                </Text>
                <List type="number">
                  <List.Item>
                    Check you have set Bloom to be required in your settings.
                  </List.Item>
                  <List.Item>
                    Ensure Bloom is loaded on your cart or product page, so it
                    can force customers to select a date.
                  </List.Item>
                  <List.Item>
                    Check if you are using any dynamic or express checkout
                    buttons, such as “Buy it now”, as Shopify don&apos;t support
                    these methods.
                  </List.Item>
                </List>

                <ButtonGroup>
                  <Button
                    variant="plain"
                    target="_blank"
                    url="https://help.shopify.com/en/manual/online-store/dynamic-checkout/compatibility"
                  >
                    Learn more
                  </Button>
                </ButtonGroup>
              </BlockStack>
            </BlockStack>
          </Card>
        </BlockStack>
      </Layout.Section>
    </PageFrame>
  )
}

function Pending() {
  return (
    <PageFrame title={`Bloom: Delivery Date & Pickups`}>
      <Layout.Section>
        <Box
          minHeight="128px"
          background="bg-fill-tertiary"
          borderRadius="200"
        ></Box>
      </Layout.Section>
      <Layout.Section variant="oneHalf">
        <BlockStack gap="400">
          <Box
            minHeight="144px"
            background="bg-fill-tertiary"
            borderRadius="200"
          ></Box>
        </BlockStack>
      </Layout.Section>
      <Layout.Section variant="oneHalf">
        <BlockStack gap="400">
          <Box
            minHeight="144px"
            background="bg-fill-tertiary"
            borderRadius="200"
          ></Box>
        </BlockStack>
      </Layout.Section>
      <Layout.Section variant="oneHalf">
        <BlockStack gap="400">
          <Box
            minHeight="144px"
            background="bg-fill-tertiary"
            borderRadius="200"
          ></Box>
          <Box
            minHeight="136px"
            background="bg-fill-tertiary"
            borderRadius="200"
          ></Box>
          <Box
            minHeight="156px"
            background="bg-fill-tertiary"
            borderRadius="200"
          ></Box>
        </BlockStack>
      </Layout.Section>
      <Layout.Section variant="oneHalf">
        <Box
          minHeight="396px"
          background="bg-fill-tertiary"
          borderRadius="200"
        ></Box>
      </Layout.Section>
    </PageFrame>
  )
}
